import React, { useState, useEffect, useRef } from 'react';
import { getToken } from '../utils/auth';

const TableSeating = () => {
  const [tables, setTables] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showTableModal, setShowTableModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [tableFormData, setTableFormData] = useState({
    table_number: '',
    capacity: '',
    position: '',
    notes: ''
  });
  
  // Assignment menu states
  const [showAssignMenu, setShowAssignMenu] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  
  // Drag state
  const [draggedGuest, setDraggedGuest] = useState(null);
  const [draggedFromTable, setDraggedFromTable] = useState(null); // Track which table guest came from
  const [dragOverGuest, setDragOverGuest] = useState(null); // Track which guest we're hovering over
  const isDragging = useRef(false);
  const isReordering = useRef(false); // Track if we're reordering within same table

  useEffect(() => {
    fetchSeatingChart();
  }, []);

  const fetchSeatingChart = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await fetch('/api/tables/seating-chart', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setTables(data.data.tables);
        setUnassigned(data.data.unassigned);
      } else {
        setError(data.error || '获取座位表失败');
      }
    } catch (err) {
      setError('获取座位表失败，请刷新页面重试');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (guest, tableId = null) => {
    setDraggedGuest(guest);
    setDraggedFromTable(tableId);
    isDragging.current = true;
    isReordering.current = false;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnd = () => {
    setDraggedGuest(null);
    setDraggedFromTable(null);
    setDragOverGuest(null);
    // Small delay before allowing clicks again
    setTimeout(() => {
      isDragging.current = false;
      isReordering.current = false;
    }, 100);
  };

  const handleDragOverGuest = (guest, tableId) => {
    // Check if we're reordering within the same table
    if (draggedGuest && draggedFromTable === tableId) {
      isReordering.current = true;
      setDragOverGuest(guest);
    }
  };

  const handleDropOnGuest = async (targetGuest, tableId) => {
    if (!draggedGuest || !isReordering.current) return;
    if (draggedGuest.rsvp_id === targetGuest.rsvp_id) return;

    try {
      const token = getToken();
      
      // Find the target position
      const table = tables.find(t => t.id === tableId);
      if (!table) return;
      
      const targetIndex = table.assignments.findIndex(a => a.rsvp_id === targetGuest.rsvp_id);
      const newSeatOrder = targetIndex + 1; // seat_order is 1-based
      
      const response = await fetch('/api/tables/reorder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          rsvp_id: draggedGuest.rsvp_id,
          new_seat_order: newSeatOrder
        })
      });

      const data = await response.json();
      if (data.success) {
        await fetchSeatingChart();
      } else {
        alert(data.error || '重新排序失败');
      }
    } catch (err) {
      alert('重新排序失败，请重试');
    } finally {
      handleDragEnd();
    }
  };

  const handleDropOnTable = async (tableId) => {
    if (!draggedGuest || isReordering.current) return;

    try {
      const token = getToken();
      const response = await fetch('/api/tables/assign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          rsvp_id: draggedGuest.id || draggedGuest.rsvp_id,
          table_id: tableId
        })
      });

      const data = await response.json();
      if (data.success) {
        await fetchSeatingChart();
      } else {
        alert(data.error || '分配失败');
      }
    } catch (err) {
      alert('分配失败，请重试');
    } finally {
      handleDragEnd();
    }
  };

  const handleMoveGuest = async (rsvpId, direction) => {
    try {
      const token = getToken();
      const response = await fetch('/api/tables/reorder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          rsvp_id: rsvpId,
          direction: direction
        })
      });

      const data = await response.json();
      if (data.success) {
        await fetchSeatingChart();
      } else {
        alert(data.error || '移动失败');
      }
    } catch (err) {
      alert('移动失败，请重试');
    }
  };

  const handleDropOnUnassigned = async () => {
    if (!draggedGuest || !draggedGuest.rsvp_id) return;

    try {
      const token = getToken();
      const response = await fetch(`/api/tables/assign/${draggedGuest.rsvp_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        await fetchSeatingChart();
      } else {
        alert(data.error || '取消分配失败');
      }
    } catch (err) {
      alert('取消分配失败，请重试');
    } finally {
      handleDragEnd();
    }
  };

  const handleGuestClick = (guest, event) => {
    // Don't open menu if we just dragged
    if (isDragging.current) return;
    
    setSelectedGuest(guest);
    setShowAssignMenu(true);
  };

  const handleAssignFromMenu = async (tableId) => {
    if (!selectedGuest) return;
    
    const guestId = selectedGuest.id || selectedGuest.rsvp_id;
    
    try {
      const token = getToken();
      const response = await fetch('/api/tables/assign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          rsvp_id: guestId,
          table_id: tableId
        })
      });

      const data = await response.json();
      if (data.success) {
        await fetchSeatingChart();
        setShowAssignMenu(false);
        setSelectedGuest(null);
      } else {
        alert(data.error || '分配失败');
      }
    } catch (err) {
      alert('分配失败，请重试');
    }
  };

  const handleUnassignFromMenu = async () => {
    if (!selectedGuest || !selectedGuest.rsvp_id) return;
    
    if (!window.confirm('确定要取消该宾客的座位分配吗？')) {
      return;
    }
    
    try {
      const token = getToken();
      const response = await fetch(`/api/tables/assign/${selectedGuest.rsvp_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        await fetchSeatingChart();
        setShowAssignMenu(false);
        setSelectedGuest(null);
      } else {
        alert(data.error || '取消分配失败');
      }
    } catch (err) {
      alert('取消分配失败，请重试');
    }
  };

  const handleUnassignGuest = async (rsvpId) => {
    if (!window.confirm('确定要取消该宾客的座位分配吗？')) return;

    try {
      const token = getToken();
      const response = await fetch(`/api/tables/assign/${rsvpId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        await fetchSeatingChart();
      } else {
        alert(data.error || '取消分配失败');
      }
    } catch (err) {
      alert('取消分配失败，请重试');
    }
  };

  const handleCreateTable = () => {
    setEditingTable(null);
    setTableFormData({ table_number: '', capacity: '', position: '', notes: '' });
    setShowTableModal(true);
  };

  const handleEditTable = (table) => {
    setEditingTable(table);
    setTableFormData({
      table_number: table.table_number,
      capacity: table.capacity,
      position: table.position || '',
      notes: table.notes || ''
    });
    setShowTableModal(true);
  };

  const handleSaveTable = async () => {
    if (!tableFormData.table_number || !tableFormData.capacity) {
      alert('请填写餐桌编号和容量');
      return;
    }

    try {
      const token = getToken();
      const url = editingTable 
        ? `/api/tables/${editingTable.id}`
        : '/api/tables';
      const method = editingTable ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(tableFormData)
      });

      const data = await response.json();
      if (data.success) {
        setShowTableModal(false);
        await fetchSeatingChart();
      } else {
        alert(data.error || '保存失败');
      }
    } catch (err) {
      alert('保存失败，请重试');
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm('确定要删除这个餐桌吗？只有未分配宾客的餐桌才能删除。')) return;

    try {
      const token = getToken();
      const response = await fetch(`/api/tables/${tableId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        await fetchSeatingChart();
      } else {
        alert(data.error || '删除失败');
      }
    } catch (err) {
      alert('删除失败，请重试');
    }
  };

  const handleExport = async () => {
    try {
      const token = getToken();
      const response = await fetch('/api/tables/export', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'wedding-seating-chart.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('导出失败，请重试');
    }
  };

  // Filter guests based on search and filter status
  const getFilteredGuests = (guestList) => {
    return guestList.filter(guest => {
      const matchesSearch = guest.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  };

  const filteredUnassigned = getFilteredGuests(unassigned);
  const filteredTables = tables.map(table => ({
    ...table,
    assignments: table.assignments.filter(guest =>
      guest.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }));

  // Calculate statistics
  const totalTables = tables.length;
  const totalCapacity = tables.reduce((sum, t) => sum + t.capacity, 0);
  const totalAssigned = tables.reduce((sum, t) => sum + t.current_occupancy, 0);
  const totalUnassigned = unassigned.reduce((sum, g) => sum + g.guests, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">加载中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button 
          onClick={fetchSeatingChart}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Statistics */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">婚礼座位安排</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{totalTables}</div>
            <div className="text-sm text-gray-600">餐桌总数</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{totalCapacity}</div>
            <div className="text-sm text-gray-600">总容量</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">{totalAssigned}</div>
            <div className="text-sm text-gray-600">已分配</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">{totalUnassigned}</div>
            <div className="text-sm text-gray-600">未分配</div>
          </div>
        </div>
      </div>

      {/* Helper Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-800">
          💡 <strong>座位分配和排序：</strong> 
          拖拽宾客到餐桌分配座位，拖拽已分配宾客可重新排序，或使用⬆️⬇️按钮调整顺序。
        </p>
      </div>

      {/* Controls Bar */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <button
            onClick={handleCreateTable}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            ➕ 创建餐桌
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            📥 导出CSV
          </button>
          <input
            type="text"
            placeholder="搜索宾客姓名..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={fetchSeatingChart}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
          >
            🔄 刷新
          </button>
        </div>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Unassigned Guests Panel */}
        <div className="lg:col-span-1">
          <div 
            className="bg-white rounded-lg shadow-md p-4 min-h-[400px]"
            onDragOver={handleDragOver}
            onDrop={handleDropOnUnassigned}
          >
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              未分配宾客 ({filteredUnassigned.length})
            </h3>
            <div className="space-y-2">
              {filteredUnassigned.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {searchTerm ? '没有匹配的宾客' : '所有宾客都已分配座位'}
                </div>
              ) : (
                filteredUnassigned.map(guest => (
                  <div
                    key={guest.id}
                    draggable
                    onDragStart={() => handleDragStart(guest)}
                    onDragEnd={handleDragEnd}
                    onClick={(e) => handleGuestClick(guest, e)}
                    className="p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-blue-300 transition"
                  >
                    <div className="font-semibold text-gray-800">{guest.name}</div>
                    <div className="text-sm text-gray-600">
                      👥 {guest.guests} 位
                      {guest.dietary_restrictions && (
                        <span className="ml-2">🍽️ {guest.dietary_restrictions}</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Tables Grid */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredTables.length === 0 ? (
              <div className="col-span-2 text-center text-gray-500 py-8">
                还没有创建餐桌，点击"创建餐桌"按钮开始
              </div>
            ) : (
              filteredTables.map(table => (
                <div
                  key={table.id}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDropOnTable(table.id)}
                  className="bg-white rounded-lg shadow-md p-4 border-2 border-dashed border-gray-300 hover:border-blue-400 transition"
                >
                  {/* Table Header */}
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-lg font-bold text-gray-800">
                          餐桌 {table.table_number}
                        </h4>
                        {table.position && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded font-medium">
                            位置 {table.position}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        容量: {table.current_occupancy}/{table.capacity}
                        <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                          table.current_occupancy === table.capacity 
                            ? 'bg-red-100 text-red-800'
                            : table.current_occupancy > table.capacity * 0.8
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {table.current_occupancy === table.capacity ? '已满' : 
                           table.current_occupancy > table.capacity * 0.8 ? '接近满' : '可用'}
                        </span>
                      </div>
                      {table.notes && (
                        <div className="text-xs text-gray-500 mt-1">📝 {table.notes}</div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEditTable(table)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="编辑"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteTable(table.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="删除"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        table.current_occupancy > table.capacity 
                          ? 'bg-red-600'
                          : table.current_occupancy === table.capacity
                          ? 'bg-yellow-600'
                          : 'bg-green-600'
                      }`}
                      style={{ width: `${Math.min((table.current_occupancy / table.capacity) * 100, 100)}%` }}
                    />
                  </div>

                  {/* Assigned Guests */}
                  <div className="space-y-2 min-h-[100px]">
                    {table.assignments.length === 0 ? (
                      <div className="text-center text-gray-400 py-4 text-sm">
                        拖拽宾客到这里分配座位
                      </div>
                    ) : (
                      table.assignments.map((guest, index) => (
                        <div
                          key={guest.rsvp_id}
                          draggable
                          onDragStart={() => handleDragStart(guest, table.id)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => {
                            e.preventDefault();
                            handleDragOverGuest(guest, table.id);
                          }}
                          onDrop={(e) => {
                            e.stopPropagation();
                            handleDropOnGuest(guest, table.id);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGuestClick(guest, e);
                          }}
                          className={`p-2 bg-blue-50 border border-blue-200 rounded cursor-move hover:bg-blue-100 transition ${
                            dragOverGuest?.rsvp_id === guest.rsvp_id && isReordering.current
                              ? 'border-blue-500 border-2 shadow-lg'
                              : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {/* Drag Handle */}
                            <div className="text-gray-400 cursor-move select-none" title="拖动重新排序">
                              ⋮⋮
                            </div>
                            
                            {/* Guest Info */}
                            <div className="flex-1">
                              <div className="font-semibold text-gray-800 text-sm">
                                {guest.name}
                              </div>
                              <div className="text-xs text-gray-600">
                                👥 {guest.guests} 位
                                {guest.dietary_restrictions && (
                                  <span className="ml-1">🍽️ {guest.dietary_restrictions}</span>
                                )}
                              </div>
                            </div>
                            
                            {/* Up/Down Buttons */}
                            <div className="flex flex-col gap-0.5">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveGuest(guest.rsvp_id, 'up');
                                }}
                                disabled={index === 0}
                                className={`p-1 text-xs ${
                                  index === 0
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : 'text-blue-600 hover:bg-blue-100 cursor-pointer'
                                } rounded transition`}
                                title="上移"
                              >
                                ⬆️
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveGuest(guest.rsvp_id, 'down');
                                }}
                                disabled={index === table.assignments.length - 1}
                                className={`p-1 text-xs ${
                                  index === table.assignments.length - 1
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : 'text-blue-600 hover:bg-blue-100 cursor-pointer'
                                } rounded transition`}
                                title="下移"
                              >
                                ⬇️
                              </button>
                            </div>
                            
                            {/* Remove Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnassignGuest(guest.rsvp_id);
                              }}
                              className="text-red-500 hover:text-red-700 text-xs p-1"
                              title="取消分配"
                            >
                              ✖
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Assignment Menu Modal */}
      {showAssignMenu && selectedGuest && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowAssignMenu(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b bg-gray-50">
              <h3 className="text-lg font-bold text-gray-800">
                分配宾客到餐桌
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {selectedGuest.name} • 👥 {selectedGuest.guests} 位
              </p>
              {selectedGuest.dietary_restrictions && (
                <p className="text-xs text-gray-500 mt-1">
                  🍽️ {selectedGuest.dietary_restrictions}
                </p>
              )}
            </div>

            {/* Table List */}
            <div className="p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">
                选择餐桌：
              </p>
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {tables.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>还没有创建餐桌</p>
                    <p className="text-sm mt-2">点击"➕ 创建餐桌"先创建餐桌</p>
                  </div>
                ) : (
                  tables.map(table => {
                    const available = table.capacity - table.current_occupancy;
                    const canFit = available >= selectedGuest.guests;
                    const isFull = available === 0;
                    
                    return (
                      <button
                        key={table.id}
                        onClick={() => handleAssignFromMenu(table.id)}
                        disabled={!canFit}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                          canFit
                            ? 'border-blue-300 bg-white hover:border-blue-500 hover:bg-blue-50 hover:shadow-md cursor-pointer'
                            : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-gray-800">
                            🪑 餐桌 {table.table_number}
                          </div>
                          <div className={`text-xs px-2 py-1 rounded ${
                            isFull 
                              ? 'bg-red-100 text-red-700' 
                              : canFit 
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {isFull ? '已满' : canFit ? '可用' : '容量不足'}
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-600 mt-1">
                          {table.current_occupancy}/{table.capacity} 座位
                          {canFit && available > 0 && (
                            <span className="text-green-600"> • {available} 个空位</span>
                          )}
                        </div>
                        
                        {table.notes && (
                          <div className="text-xs text-gray-500 mt-1 italic">
                            📝 {table.notes}
                          </div>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t bg-gray-50 flex gap-2">
              <button
                onClick={() => {
                  setShowAssignMenu(false);
                  setSelectedGuest(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition font-medium"
              >
                取消
              </button>
              
              {selectedGuest.rsvp_id && (
                <button
                  onClick={handleUnassignFromMenu}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-medium"
                >
                  🗑️ 取消分配
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table Modal */}
      {showTableModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              {editingTable ? '编辑餐桌' : '创建新餐桌'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  餐桌编号 *
                </label>
                <input
                  type="text"
                  value={tableFormData.table_number}
                  onChange={(e) => setTableFormData({ ...tableFormData, table_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="例如: 1, A1, 主桌"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  容量 *
                </label>
                <input
                  type="number"
                  min="1"
                  value={tableFormData.capacity}
                  onChange={(e) => setTableFormData({ ...tableFormData, capacity: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="座位数量"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  位置编号 (1-12)
                </label>
                <select
                  value={tableFormData.position}
                  onChange={(e) => setTableFormData({ ...tableFormData, position: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">无位置</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(pos => {
                    const isOccupied = tables.some(t => 
                      t.position === pos && t.id !== editingTable?.id
                    );
                    return (
                      <option key={pos} value={pos} disabled={isOccupied}>
                        位置 {pos} {isOccupied ? '(已占用)' : ''}
                      </option>
                    );
                  })}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  选择餐桌在首页展示的位置 (可选)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  备注
                </label>
                <textarea
                  value={tableFormData.notes}
                  onChange={(e) => setTableFormData({ ...tableFormData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="例如: 家人桌, 朋友桌"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveTable}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                保存
              </button>
              <button
                onClick={() => setShowTableModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableSeating;

